import { Workflow } from "./interface";

export const BLOCK_ICON_MAP: Record<string, string> = {
  GOOGLE_DOCS: `${process.env.REACT_APP_STATIC_URL}/docs-icon.svg`,
  GOOGLE_SLIDES: `${process.env.REACT_APP_STATIC_URL}/slides-icon.svg`,
  GOOGLE_SHEETS: `${process.env.REACT_APP_STATIC_URL}/sheets-icon.svg`,
  GOOGLE_FORMS: `${process.env.REACT_APP_STATIC_URL}/forms-icon.svg`,
  GOOGLE_DRIVE: `${process.env.REACT_APP_STATIC_URL}/drive-icon.svg`,
  GMAIL: `${process.env.REACT_APP_STATIC_URL}/gmail-icon.svg`,
  SIGNATURE_REQUEST: `${process.env.REACT_APP_STATIC_URL}/portant-sign-icon.svg`,
  WEBHOOK: `${process.env.REACT_APP_STATIC_URL}/webhook-icon.svg`,
  TALLY: `${process.env.REACT_APP_STATIC_URL}/tally-icon.png`,
  HUBSPOT: `${process.env.REACT_APP_STATIC_URL}/hubspot-icon.svg`,
  GLIDE: `${process.env.REACT_APP_STATIC_URL}/glide-icon.svg`,
  GLIDE_SHARE: `${process.env.REACT_APP_STATIC_URL}/glide-icon.svg`,
  PDF: `${process.env.REACT_APP_STATIC_URL}/pdf-icon.svg`,
  PDF_FILLABLE: `${process.env.REACT_APP_STATIC_URL}/pdf-icon.svg`,
  PDF_OVERLAY: `${process.env.REACT_APP_STATIC_URL}/pdf-icon.svg`,
  NONE: `${process.env.REACT_APP_STATIC_URL}/dummy.svg`,
  WRITE_TO_SOURCE: `${process.env.REACT_APP_STATIC_URL}/sheets-icon.svg`,
  XERO: `${process.env.REACT_APP_STATIC_URL}/xero-icon.svg`,
  REVIEW: `${process.env.REACT_APP_STATIC_URL}/portant-review-icon.svg`,
  APPROVAL: `${process.env.REACT_APP_STATIC_URL}/dummy.svg`,
  APRIL_FOOLS: `${process.env.REACT_APP_STATIC_URL}/april-fools.png`,
  CHAT_GPT: `${process.env.REACT_APP_STATIC_URL}/openai-icon.png`,
  MICROSOFT: `${process.env.REACT_APP_STATIC_URL}/microsoft-icon.svg`,
  MICROSOFT_EXCEL: `${process.env.REACT_APP_STATIC_URL}/microsoft-excel-icon.svg`,
  MICROSOFT_WORD: `${process.env.REACT_APP_STATIC_URL}/microsoft-word-icon.svg`,
  MICROSOFT_POWERPOINT: `${process.env.REACT_APP_STATIC_URL}/microsoft-powerpoint-icon.svg`,
  OUTLOOK: `${process.env.REACT_APP_STATIC_URL}/microsoft-outlook-icon.svg`,
  ZAPIER: `${process.env.REACT_APP_STATIC_URL}/zapier-icon.svg`,
  COPPER: `${process.env.REACT_APP_STATIC_URL}/copper-icon.png`,
  STRIPE: `${process.env.REACT_APP_STATIC_URL}/stripe-logo.png`,
  GOOGLE_SHEETS_MERGE: `${process.env.REACT_APP_STATIC_URL}/sheets-icon.svg`,
  GOOGLE_SHEETS_APPEND: `${process.env.REACT_APP_STATIC_URL}/sheets-icon.svg`,
  SIGNATURE_LINK: `${process.env.REACT_APP_STATIC_URL}/portant-sign-icon.svg`,
};

const OUTPUT_TYPE_LABEL: Record<string, string> = {
  GOOGLE_DOCS: "Docs",
  GOOGLE_SLIDES: "Slides",
  GOOGLE_SHEETS: "Sheets",
  GOOGLE_SHEETS_MERGE: "Sheets",
  GOOGLE_SHEETS_APPEND: "Sheets",
  GOOGLE_FORMS: "Forms",
  GOOGLE_DRIVE: "Drive",
  GMAIL: "Gmail",
  WRITE_TO_SOURCE: "Sheets",
  GLIDE: "Glide",
  GLIDE_SHARE: "Glide",
  PDF: "PDF",
  PDF_FILLABLE: "PDF",
  PDF_OVERLAY: "PDF",
  MICROSOFT_WORD: "Word",
  MICROSOFT_POWERPOINT: "PowerPoint",
  MICROSOFT_EXCEL: "Excel",
  MICROSOFT: "Microsoft",
  OUTLOOK: "Outlook",
  SIGNATURE_REQUEST: "Sign",
  SIGNATURE_LINK: "Sign",
  HUBSPOT: "HubSpot",
  WEBHOOK: "Webhook",
  REVIEW: "Review",
};

const PDF_OUTPUT_TYPE_ORDER = ["PDF", "PDF_FILLABLE", "PDF_OVERLAY"];

const GOOGLE_OUTPUT_TYPE_ORDER = [
  "GOOGLE_DOCS",
  "GOOGLE_SLIDES",
  "GOOGLE_SHEETS",
  "GOOGLE_SHEETS_MERGE",
  "GOOGLE_SHEETS_APPEND",
  "GOOGLE_FORMS",
  "GOOGLE_DRIVE",
  "GMAIL",
  "WRITE_TO_SOURCE",
  "GLIDE",
  "GLIDE_SHARE",
];

const MICROSOFT_OUTPUT_TYPE_ORDER = [
  "MICROSOFT_WORD",
  "MICROSOFT_POWERPOINT",
  "MICROSOFT_EXCEL",
  "MICROSOFT",
  "OUTLOOK",
];

function getOutputTypeBucket(key: string): number {
  if (key === "PDF" || key.startsWith("PDF_"))
    return 0;
  if (
    key.startsWith("GOOGLE_") ||
    key === "GMAIL" ||
    key === "WRITE_TO_SOURCE" ||
    key === "GLIDE" ||
    key === "GLIDE_SHARE"
  )
    return 1;
  if (key.startsWith("MICROSOFT_") || key === "OUTLOOK")
    return 2;
  return 3;
}

function getOutputTypeIndexInBucket(key: string): number {
  const lists = [PDF_OUTPUT_TYPE_ORDER, GOOGLE_OUTPUT_TYPE_ORDER, MICROSOFT_OUTPUT_TYPE_ORDER];
  const bucket = getOutputTypeBucket(key);
  if (bucket === 3)
    return Number.MAX_SAFE_INTEGER;
  const index = lists[bucket].indexOf(key);
  return index === -1 ? Number.MAX_SAFE_INTEGER - 1 : index;
}

function compareOutputTypeKeys(a: string, b: string): number {
  const bucketDiff = getOutputTypeBucket(a) - getOutputTypeBucket(b);
  if (bucketDiff !== 0)
    return bucketDiff;

  const indexDiff = getOutputTypeIndexInBucket(a) - getOutputTypeIndexInBucket(b);
  if (indexDiff !== 0)
    return indexDiff;

  return a.localeCompare(b);
}

function collectWorkflowOutputTypeKeys(workflow: Workflow): Array<string> {
  const fromApi = workflow.previewAllOutputTypes ?? [];
  if (fromApi.length > 0)
    return [...fromApi].sort(compareOutputTypeKeys);

  const keys: Array<string> = [];

  const addKey = (key?: string | null) => {
    if (key)
      keys.push(key);
  };

  (workflow.documentTypes ?? []).forEach(addKey);

  const documents = workflow.documents ?? [];
  documents.forEach(document => {
    addKey(document.documentType);
    if (document.createPdfCopy)
      addKey("PDF");
  });

  if (keys.length === 0)
    addKey(workflow.previewDocumentType);

  return keys.sort(compareOutputTypeKeys);
}

function getOutputTypeLabel(key: string): string {
  if (OUTPUT_TYPE_LABEL[key])
    return OUTPUT_TYPE_LABEL[key];

  return key
    .replace(/^GOOGLE_/, "")
    .replace(/^MICROSOFT_/, "")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export interface WorkflowOutputCardPill {
  iconUrl: string;
  label: string;
}

const CARD_MAX_VISIBLE_OUTPUT_PILLS = 3;

/** Card grid: up to 3 unique type pills, +N for hidden types and duplicate outputs. */
export function getWorkflowOutputCardPills(workflow: Workflow): {
  pills: Array<WorkflowOutputCardPill>;
  overflowCount: number;
} {
  const keys = collectWorkflowOutputTypeKeys(workflow);
  const uniquePills: Array<WorkflowOutputCardPill> = [];
  const seenIconUrls = new Set<string>();

  for (const key of keys) {
    const iconUrl = BLOCK_ICON_MAP[key];
    if (!iconUrl || seenIconUrls.has(iconUrl))
      continue;
    seenIconUrls.add(iconUrl);
    uniquePills.push({ iconUrl, label: getOutputTypeLabel(key) });
  }

  const pills = uniquePills.slice(0, CARD_MAX_VISIBLE_OUTPUT_PILLS);
  const visiblePillCount = pills.length;

  return {
    pills,
    overflowCount: Math.max(0, keys.length - visiblePillCount),
  };
}

/** CDN icon URLs for a workflow's outputs (one icon per output; list view). */
export function getWorkflowOutputIconUrls(workflow: Workflow): Array<string> {
  const icons: Array<string> = [];

  for (const key of collectWorkflowOutputTypeKeys(workflow)) {
    const icon = BLOCK_ICON_MAP[key];
    if (icon)
      icons.push(icon);
  }

  return icons;
}

export const MIMETYPE_ICON_MAP: Record<string, string> = {
  "application/vnd.google-apps.file": `${process.env.REACT_APP_STATIC_URL}/file-icon.svg`,
  "application/vnd.google-apps.folder": `${process.env.REACT_APP_STATIC_URL}/folder-icon.svg`,
  'application/vnd.google-apps.document': `${process.env.REACT_APP_STATIC_URL}/docs-icon.svg`,
  'application/vnd.google-apps.presentation': `${process.env.REACT_APP_STATIC_URL}/slides-icon.svg`,
  'application/vnd.google-apps.spreadsheet': `${process.env.REACT_APP_STATIC_URL}/sheets-icon.svg`,
  'application/vnd.google-apps.form': `${process.env.REACT_APP_STATIC_URL}/forms-icon.svg`,
  "application/vnd.google-apps.photo": `${process.env.REACT_APP_STATIC_URL}/image-icon.svg`,
  'application/pdf': `${process.env.REACT_APP_STATIC_URL}/pdf-icon.svg`,
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": `${process.env.REACT_APP_STATIC_URL}/docx-icon.svg`,
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": `${process.env.REACT_APP_STATIC_URL}/pptx-icon.svg`,
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": `${process.env.REACT_APP_STATIC_URL}/xlsx-icon.svg`,
  'image/*': `${process.env.REACT_APP_STATIC_URL}/image-icon.svg`,
}


export const RECIPIENT_COLOUR_MAP: Record<number, Array<string>> = {
  1: ["text-blue", "bg-blue"],
  2: ["text-purple", "bg-purple"],
  3: ["text-green", "bg-green"],
  4: ["text-red", "bg-red"],
}

export const EMAIL_REGEX = /(.+@.+\..+)|(\{\{.+\}\})/;

export const INSTANT_WORKFLOW_SLUGS = ["quotes", "proposals", "contracts"];
